import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Loader2, MessageCircle, Clock, QrCode, Smartphone } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";

const ITEM_LABEL = { archaka_subscription: "అర్చక నెలవారీ సభ్యత్వం (30 రోజులు)", archaka_unlock: "అర్చక కాంటాక్ట్ అన్‌లాక్", temple_unlock: "దేవాలయ కాంటాక్ట్ అన్‌లాక్", credit_pack: "క్రెడిట్ ప్యాక్" };

const Step = ({ n, title, children }) => (
  <div className="flex gap-3">
    <div className="w-7 h-7 shrink-0 rounded-full bg-saffron text-white text-sm font-bold flex items-center justify-center">{n}</div>
    <div className="flex-1 space-y-2"><p className="font-semibold text-maroon text-sm">{title}</p>{children}</div>
  </div>
);

export default function PaymentModal({ open, onClose, itemType, itemId, pkg = "standard", onSuccess }) {
  const { settings } = useApp();
  const [shot, setShot] = useState("");
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { if (open) { setShot(""); setUtr(""); setDone(null); } }, [open]);

  const s = settings || {};
  const amount = itemType === "archaka_subscription" ? Number(s.archaka_monthly_fee || 499) : itemType === "credit_pack" ? (pkg === "standard" ? s.credit_pack_fee ?? 199 : s.credit_pack_premium ?? 299) : s.contact_unlock_fee ?? 99;
  const phone = String(s.helpline_phone || "9603333685").replace(/\D/g, "").slice(-10);
  const upi = s.payment_upi_id || "";
  const payee = s.payment_payee_name || s.founder_name || "ArchakaVeda";
  const upiLink = upi ? `upi://pay?pa=${encodeURIComponent(upi)}&pn=${encodeURIComponent(payee)}&am=${amount}&cu=INR&tn=${encodeURIComponent("ArchakaVeda " + (ITEM_LABEL[itemType] || ""))}` : "";

  const copyUpi = () => { navigator.clipboard?.writeText(upi); toast.success("UPI ID కాపీ అయింది"); };

  const submit = async () => {
    if (!shot) return toast.error("ముందుగా చెల్లింపు screenshot upload చేయండి");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/manual", { item_type: itemType, item_id: itemId, package: pkg, screenshot_url: shot, utr });
      setDone(data); onSuccess && onSuccess(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "సమర్పణ విఫలమైంది. మళ్ళీ ప్రయత్నించండి.");
    } finally { setBusy(false); }
  };

  const waText = done ? `నమస్కారం 🙏 ArchakaVeda లో ₹${done.amount} చెల్లించాను (${ITEM_LABEL[itemType] || itemType}).\nచెల్లింపు ID: ${done.txn_id}${utr ? `\nUTR: ${utr}` : ""}\nస్క్రీన్‌షాట్ జత చేస్తున్నాను. దయచేసి ఆమోదించండి.` : "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-surface max-h-[92vh] overflow-y-auto" data-testid="payment-modal">
        <DialogHeader><DialogTitle className="font-display text-maroon flex items-center gap-2"><QrCode className="w-5 h-5 text-saffron" /> QR కోడ్‌తో చెల్లింపు</DialogTitle></DialogHeader>

        {!done ? (
          <div className="space-y-5 font-telugu">
            <div className="text-center p-3 rounded-xl bg-marigold/10 border border-goldline/40">
              <p className="text-xs text-muted-foreground">{ITEM_LABEL[itemType]}</p>
              <p className="text-3xl font-bold text-saffron" data-testid="pay-amount">₹{amount}</p>
            </div>

            <Step n="1" title={`ఈ QR కోడ్‌ను స్కాన్ చేసి ₹${amount} చెల్లించండి`}>
              {s.payment_qr_url ? (
                <img src={s.payment_qr_url} alt="Payment QR" data-testid="pay-qr-image" className="w-56 h-56 object-contain mx-auto rounded-xl border-2 border-goldline/50 bg-white p-2" />
              ) : (
                <div data-testid="pay-qr-missing" className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 text-center">QR కోడ్ త్వరలో అందుబాటులోకి వస్తుంది. దయచేసి {phone} కు కాల్ చేయండి.</div>
              )}
              <p className="text-[11px] text-muted-foreground text-center">PhonePe · Google Pay · Paytm · ఏ UPI యాప్ అయినా</p>
              {upi && (
                <div className="flex items-center justify-between gap-2 p-2 rounded-lg border border-goldline/40 bg-white">
                  <div className="text-xs"><span className="text-muted-foreground">UPI ID: </span><b className="text-maroon" data-testid="pay-upi-id">{upi}</b><div className="text-muted-foreground">{payee}</div></div>
                  <Button size="sm" variant="outline" data-testid="pay-copy-upi" onClick={copyUpi} className="h-8"><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              )}
              {upiLink && <a href={upiLink} data-testid="pay-open-upi-app" className="sm:hidden flex items-center justify-center gap-2 w-full py-2 rounded-full border border-saffron text-saffron text-sm"><Smartphone className="w-4 h-4" />UPI యాప్‌లో నేరుగా చెల్లించండి</a>}
            </Step>

            <Step n="2" title="చెల్లింపు పూర్తయిన తర్వాత ఆ screenshot ను ఇక్కడ upload చేయండి">
              <FileUpload accept="image/*" value={shot} onChange={setShot} testid="pay-screenshot-upload" />
              <Input data-testid="pay-utr" value={utr} onChange={(e) => setUtr(e.target.value)} maxLength={30} placeholder="UTR / Transaction ID (ఉంటే)" className="gold-border text-sm" />
            </Step>

            <Step n="3" title="సమర్పించండి, ఆ తర్వాత అదే screenshot ను మాకు WhatsApp లో పంపండి">
              <Button data-testid="pay-submit-proof" onClick={submit} disabled={busy || !shot} className="w-full btn-sacred border-0">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "చెల్లింపు వివరాలు సమర్పించండి"}
              </Button>
            </Step>

            <p className="text-[11px] text-muted-foreground text-center">మేము మీ చెల్లింపును చెక్ చేసి ఆమోదించిన వెంటనే 30 రోజుల పాటు అన్ని దేవాలయాల ఫోన్ నంబర్, చిరునామా కనిపిస్తాయి (సాధారణంగా కొన్ని గంటల్లో).</p>
          </div>
        ) : (
          <div className="space-y-4 font-telugu text-center" data-testid="pay-submitted">
            <Clock className="w-14 h-14 text-saffron mx-auto" />
            <p className="text-lg font-bold text-maroon">మీ చెల్లింపు పరిశీలనలో ఉంది</p>
            <p className="text-sm text-muted-foreground">చెల్లింపు ID: <b className="text-maroon" data-testid="pay-txn-id">{done.txn_id}</b></p>
            <div className="p-3 rounded-xl bg-[#25D366]/10 border border-[#25D366]/40 text-left text-sm space-y-1">
              <p className="font-semibold text-[#128C7E]">చివరి దశ: screenshot ను WhatsApp లో పంపండి</p>
              <p className="text-xs text-muted-foreground">కింద బటన్ నొక్కితే WhatsApp తెరుచుకుంటుంది. మెసేజ్ ఇప్పటికే రాసి ఉంటుంది; మీ చెల్లింపు screenshot ను జత చేసి పంపండి.</p>
            </div>
            <a data-testid="pay-whatsapp-send" href={`https://wa.me/91${phone}?text=${encodeURIComponent(waText)}`} target="_blank" rel="noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold transition-colors">
              <MessageCircle className="w-5 h-5" /> WhatsApp లో screenshot పంపండి ({phone})
            </a>
            <p className="text-xs text-muted-foreground">ఆమోదించిన వెంటనే మీకు నోటిఫికేషన్ వస్తుంది. స్థితిని మీ డాష్‌బోర్డ్‌లో చూడవచ్చు.</p>
            <Button variant="outline" data-testid="pay-close" onClick={onClose} className="w-full">సరే</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
